import { DateTime as LDateTime } from "luxon";

// #region US only
//

export const domesticTimezoneIanas = [
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
] as const;

export type DomesticTimezoneIana = (typeof domesticTimezoneIanas)[number];

export const domesticTimezoneIanaToCode: {
  [_ in DomesticTimezoneIana]: string;
} = {
  "America/Chicago": "CT",
  "America/Denver": "MT",
  "America/Los_Angeles": "PT",
  "America/New_York": "ET",
} as const;

export function isDomesticTimezoneIana(
  val?: unknown,
): val is DomesticTimezoneIana {
  if (!val || typeof val !== "string") return false;
  return val in domesticTimezoneIanaToCode;
}

export function userDomesticTimezoneIana(): DomesticTimezoneIana {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    domesticTimezoneIanas.find((iana) => iana === tz) ??
    domesticTimezoneIanas[0]
  );
}

// #endregion

// #region International
//

export const internationalTimezoneIanas = [
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
  "Asia/Tokyo",
  "Asia/Hong_Kong",
  "Europe/London",
  "Australia/Sydney",
  "Australia/Darwin",
  "Australia/Perth",
  "Australia/Brisbane",
  "Australia/Melbourne",
  "Australia/Adelaide",
  "Australia/Hobart",
  "Australia/Broken_Hill",
  "Australia/Lord_Howe",
  "Australia/Eucla",
  "Australia/Lindeman",
  "Australia/Currie",
] as const;

export type InternationalTimezoneIana =
  (typeof internationalTimezoneIanas)[number];

export const internationalTimezoneIanaToCode: {
  [key: string]: string;
} = {
  "America/Chicago": "CT",
  "America/Denver": "MT",
  "America/Los_Angeles": "PT",
  "America/New_York": "ET",
  "Asia/Tokyo": "JST",
  "Asia/Hong_Kong": "HKT",
  "Europe/London": "Europe/London",
  "Australia/Sydney": "AEST",
  "Australia/Darwin": "ACST",
  "Australia/Perth": "AWST",
  "Australia/Brisbane": "AEST",
  "Australia/Melbourne": "AEST",
  "Australia/Adelaide": "ACST",
  "Australia/Hobart": "AEST",
  "Australia/Broken_Hill": "ACST",
  "Australia/Lord_Howe": "LHST",
  "Australia/Eucla": "ACWST",
  "Australia/Lindeman": "AEST",
  "Australia/Currie": "AEST",
} as const;

export const internationalTimezoneIanaToCodeForDST: { [key: string]: string } =
  {
    "Australia/Sydney": "AEDT",
    "Australia/Adelaide": "ACDT",
    "Australia/Hobart": "AEDT",
    "Australia/Broken_Hill": "ACDT",
    "Australia/Lord_Howe": "LHDT",
    "Australia/Currie": "AEDT",
    "Australia/Melbourne": "AEDT",
  } as const;

export function isInternationalTimezoneIana(
  val?: unknown,
): val is InternationalTimezoneIana {
  if (!val || typeof val !== "string") return false;
  return val in internationalTimezoneIanaToCode;
}

export const getTimeZoneIana = (dateTime: LDateTime): string => {
  const isDomestic = isDomesticTimezoneIana(dateTime.zoneName);
  if (!isDomestic) {
    return getInternationalTimezoneCodeFromDateTime(dateTime);
  }
  return domesticTimezoneIanaToCode[dateTime.zoneName];
};
/*

 */
const getInternationalTimezoneCodeFromDateTime = (
  dateTime: LDateTime,
): string => {
  let timeZoneCode = "";
  if (dateTime.isInDST) {
    timeZoneCode = internationalTimezoneIanaToCodeForDST[dateTime.zoneName];
  } else {
    timeZoneCode = internationalTimezoneIanaToCode[dateTime.zoneName];
  }
  return timeZoneCode || "";
};

// #endregion
